// @flow
import React, { Fragment, useContext } from 'react';

import ratios from 'Components/utils/ratios/ratios.utils.js';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { truncate } from 'Components/utils/string/string.util';
import { t } from 'Src/i18n';
import { ICONS } from 'Components/utils/icon/icons';
import { COLORS } from 'Components/utils/color/colors';

import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import Image from 'Components/frameworks/image/Image.jsx';
import { TextSeeMore } from 'Components/frameworks/text-see-more/TextSeeMore.jsx';
import { Title } from 'Components/frameworks/title/Title.jsx';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';

type Props = {
  imageSrc?: string,
  link?: {
    text: string,
    url: string,
  },
  text?: string,
  title?: string,
};

export function ProductSpaceIntroduction({ imageSrc, text, title, link }: Props) {
  const { isTablet } = useContext(ResponsiveContext);
  if (!title) {
    return null;
  }

  const logo = (
    <div className="md:text-initial">
      <Icon isNotSquare name={ICONS.CLUBMED_EXCLUSIVE} width="calc(160rem / 16)" />
    </div>
  );

  const content = (
    <Fragment>
      <Title className="mb-5" theme="title-serif-2" color={COLORS.WHITE}>
        {truncate(title, 50)}
      </Title>
      <TextSeeMore
        color={COLORS.WHITE}
        hasNoScrollOnCollapse
        isCollapseEnabled
        seeLessLabel={t('common.loadLess')}
        seeMoreLabel={t('common.viewMore')}
        seeMoreSize={80}
        seeMoreTheme="deepBlue"
        text={text}
        theme="text-sans-3"
      />
    </Fragment>
  );

  const image = (
    <Image
      imageUrl={imageSrc}
      className="rounded-tiny overflow-hidden inset-border-after--large"
      ratioKey={ratios.R_13_BY_12}
    />
  );

  const spaceLink = link ? (
    <DynamicLink url={link.url} className="block mt-6 text-white underline">
      {link.text}
    </DynamicLink>
  ) : null;

  return isTablet ? (
    <article className="font-serif" style={{ margin: '100px 0 75px' }}>
      <div className="flex bg-deepBlue rounded-tiny">
        <div className="w-1/2 p-5 lg:p-10 xl:p-20 xl:rtl:pr-30 text-white">
          <div className="mb-8 text-white text-center">{logo}</div> {content} {spaceLink}
        </div>
        <div className="h-full relative w-12/25 -my-9 self-center image-responsive-cell">
          {image}
        </div>
      </div>
    </article>
  ) : (
    <article className="font-serif">
      <div className="relative image-responsive-cell">{image}</div>
      <div className="bg-deepBlue rounded-tiny px-6 py-10 text-center font-serif">
        <div className="sm:inline-block mb-8 text-white text-center">{logo}</div>
        {content}
        {spaceLink}
      </div>
    </article>
  );
}
